import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { BannerNotificationsService, LocalStorageService, PaginatedList } from '../../../xform-compat';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subject } from 'rxjs';
import { CONFIG } from '../../../config';
import { CompanyBasic, Indication, Product } from '../../../shared/models';
import { AutocompleteService, UserGroupsService } from '../../../core/data-services';
import { SearchArrowNavigationService } from './search-arrow-navigation.service';
import { escapeHtml } from '../../../shared/utils/escape-html';

export type AvailableColumnValue = 0 | 1 | 2;

export interface ArrowNavConf {
  selectedColumn: AvailableColumnValue;
  selectedRow: number;
}

/**
 * Header search component with autocomplete functionality.
 * Expands from an icon to a full search bar with results.
 */
@Component({
  selector: 'xform-header-search',
  templateUrl: './header-search.component.html',
  styleUrls: ['./header-search.component.scss'],
  standalone: false
})
export class HeaderSearchComponent implements OnDestroy, OnInit {
  @ViewChild('search', { static: true }) searchField: ElementRef;

  @Output() searchInputDetected = new EventEmitter<string>();
  @Output() quickSearchOpen = new EventEmitter<boolean>();
  @Output() closeSearchPortal = new EventEmitter<void>();

  private defaultParams = { ...CONFIG.defaultGetParams, pagesize: 7 };
  private defaultNavPosition: ArrowNavConf = {
    selectedColumn: 0,
    selectedRow: -1
  };
  private clickListener: () => void;

  public companyList: Array<CompanyBasic> = [];
  public indicationsList: Array<Indication> = [];
  public productList: Array<Product> = [];
  public searchValue = '';
  public openSearch = false;
  public blurSearchResults = false;
  public arrowNavPosition: ArrowNavConf = this.defaultNavPosition;
  public entitiesSearch$ = new Subject<string>();
  public destroy$ = new Subject<null>();
  public resultLinkTarget = '_blank';

  public readonly SEARCH_BUTTON_ID = 'search-button';

  @HostListener('window:keydown.meta.s', ['$event'])
  @HostListener('window:keydown.control.s', ['$event'])
  showPinned(event: KeyboardEvent): void {
    event.preventDefault();
    if (!this.openSearch) {
      this.openSearch = true;
      this.blurSearchResults = false;
      this.quickSearchOpen.emit(true);
      this.setUserSettings();
    }
    this.setAutoFocus();
  }

  constructor(
    private bannerNotificationService: BannerNotificationsService,
    private autocompleteService: AutocompleteService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private searchArrowNavigationService: SearchArrowNavigationService,
    private localStorageService: LocalStorageService,
    private userGroupService: UserGroupsService
  ) {}

  ngOnInit(): void {
    this.setupClickOutsideListener();
    this.enableSearch();
  }

  ngOnDestroy(): void {
    if (this.clickListener) {
      this.clickListener();
    }
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  /**
   * Sets up listener to close search when clicking outside
   */
  private setupClickOutsideListener(): void {
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (this.openSearch && !this.elementRef.nativeElement.contains(event.target)) {
        this.closeSearch(true);
      }
    });
  }

  private resetLists(): void {
    this.companyList = [];
    this.indicationsList = [];
    this.productList = [];
  }

  public closeQuickSearch(): void {
    this.closeSearch(false);
    this.closeSearchPortal.emit();
  }

  /**
   * Handles toggle button click - stops propagation to prevent click-outside from firing
   */
  public onToggleClick(event: Event): void {
    event.stopPropagation();
    this.toggleSearchContainer();
  }

  /**
   * Toggles the search container open/closed
   */
  public toggleSearchContainer(): void {
    if (this.openSearch) {
      this.closeSearch(false);
    } else {
      this.openSearch = true;
      this.blurSearchResults = false;
      this.quickSearchOpen.emit(true);
      this.setUserSettings();
      this.setAutoFocus();
    }
  }

  /**
   * Closes the search container
   */
  private closeSearch(keepData: boolean): void {
    this.openSearch = false;
    this.blurSearchResults = true;
    if (!keepData) {
      this.searchValue = '';
      this.resetLists();
      this.searchInputDetected.emit(this.searchValue);
    }
    this.quickSearchOpen.emit(false);
  }

  /**
   * Handles search input changes
   */
  public onSearchChange(value: string): void {
    if (this.searchValue) {
      const searchTerm = value.replace(/^\?|^"/, '');
      this.defaultParams['q'] = this.searchValue;
      this.entitiesSearch$.next(searchTerm);
      this.searchInputDetected.emit(searchTerm);
    }
  }

  private enableSearch(): void {
    this.entitiesSearch$.pipe(
      distinctUntilChanged(),
      debounceTime(200),
      switchMap(() => this.getEntitiesData()),
      takeUntil(this.destroy$)
    ).subscribe(
      (entitiesResponseLists: [PaginatedList<CompanyBasic>, PaginatedList<Indication>, PaginatedList<Product>]) => {
        if (this.searchValue !== '') {
          this.blurSearchResults = false;
          this.transformResultsAndFillLists(entitiesResponseLists);
        } else {
          this.resetLists();
        }
      },
      error => this.bannerNotificationService.error(error)
    );
  }

  private getEntitiesData(): Observable<[PaginatedList<CompanyBasic>, PaginatedList<Indication>, PaginatedList<Product>]> {
    return forkJoin([
      this.autocompleteService.autocompleteEntity<CompanyBasic>('companies', { ...this.defaultParams, pagesize: 10 }),
      this.autocompleteService.autocompleteEntity<Indication>('indications', { ...this.defaultParams, excludePartial: true }),
      this.autocompleteService.autocompleteEntity<Product>('products', { ...this.defaultParams, excludePartial: true })
    ]);
  }

  private transformResultsAndFillLists(lists: [PaginatedList<CompanyBasic>, PaginatedList<Indication>, PaginatedList<Product>]): void {
    lists.forEach(list => {
      list.records.forEach(record => {
        record.nameSelected = this.formattedHighlight(record.name);
      });
    });
    const [companies, indications, products] = lists;
    this.companyList = companies.records;
    this.indicationsList = indications.records;
    this.productList = products.records;
    this.searchArrowNavigationService.entitiesCount = [this.companyList.length, this.indicationsList.length, this.productList.length];
  }

  private formattedHighlight(text: string): string {
    const escapedText = escapeHtml(text);
    // Escape special regex characters in search value to prevent errors
    const escapedSearchValue = this.searchValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const reg = '(' + escapeHtml(escapedSearchValue) + ')';
    const regex = new RegExp(reg, 'i');
    const match = escapedText.match(regex);
    if (!match) {
      return escapedText;
    }
    const matchStartPosition = match.index;
    const matchEndPosition = matchStartPosition + match[0].length;
    const originalTextFoundByRegex = escapedText.substring(matchStartPosition, matchEndPosition);
    return escapedText.replace(regex, `<span class="bold">${originalTextFoundByRegex}</span>`);
  }

  /**
   * On exact ticker match, navigate to company
   */
  public compareToTicker(): void {
    const tickerMatch = this.companyList.find(
      item => item.ticker?.toLowerCase() === this.searchValue.toLowerCase()
    );
    if (tickerMatch) {
      const url = `/company/${tickerMatch.id}`;
      if (this.resultLinkTarget === '_self') {
        window.location.replace(url);
      } else {
        window.open(url);
      }
      this.closeQuickSearch();
    }
  }

  public arrowNavigationListener(direction: string, e: KeyboardEvent): void {
    e.stopPropagation();
    if (direction !== 'left' && direction !== 'right') {
      this.arrowNavPosition = this.searchArrowNavigationService.moveUpAndDown(direction, this.arrowNavPosition);
      e.preventDefault();
    }
  }

  public openLinkArrowSearch(): void {
    if (!this.openSearch || this.arrowNavPosition.selectedRow < 0) {
      return;
    }

    const entities = [
      { urlPrefix: 'company', list: this.companyList },
      { urlPrefix: 'indications', list: this.indicationsList },
      { urlPrefix: 'product', list: this.productList }
    ];

    const entity = entities[this.arrowNavPosition.selectedColumn];
    const item = entity.list[this.arrowNavPosition.selectedRow];

    if (item) {
      const url = `/${entity.urlPrefix}/${item.id}`;
      if (this.resultLinkTarget === '_self') {
        window.location.replace(url);
      } else {
        window.open(url);
      }
    }
  }

  public clearSearchTerm(): void {
    this.searchValue = '';
    this.defaultParams['q'] = this.searchValue;
    this.entitiesSearch$.next(this.searchValue);
    this.searchInputDetected.emit(this.searchValue);
    this.arrowNavPosition = this.defaultNavPosition;
    this.setAutoFocus();
    this.resetLists();
  }

  private setUserSettings(): void {
    const loggedUser = this.localStorageService.get('loggedUser');
    if (loggedUser?.id) {
      this.userGroupService.getUserSettings(loggedUser.id).subscribe(settings => {
        this.resultLinkTarget = settings.displayResultNewWindow ? '_self' : '_blank';
      });
    }
  }

  private setAutoFocus(): void {
    setTimeout(() => {
      this.searchField?.nativeElement?.focus();
    }, 100);
  }
}
